import { v4 as uuidv4 } from "uuid";
import type firebase from "firebase/compat/app";
import db, { dbv9 } from "../utils/firestore";
import {
    ConsentForm,
    EmailRecipientType,
    IEmailReminder,
    INewOutboundEmail,
    IOutboundEmail,
    OutboundEmailStatus,
    OutboundEmailType,
    PortalSupportedFormType,
    UpdatedFrom,
} from "intake_shared/src/types";
import { Collection } from "intake_shared/src/enum";
import { sanitiseEmailRecipients } from "../utils/email";
import { isValidEmail } from "../utils/utils";
import {
    DocumentData,
    doc,
    increment,
    serverTimestamp,
    UpdateData,
    writeBatch,
} from "firebase/firestore";

function getQueuedEmail(
    outbound_email: INewOutboundEmail,
    status: OutboundEmailStatus
) {
    const id = uuidv4();
    const todays_date = new Date();
    const updated_outbound_email: IOutboundEmail | INewOutboundEmail = {
        ...outbound_email,
        id,
        created_at: todays_date,
        updated_at: todays_date,
        status,
        retries: 0,
        last_error: {},
        sender: { email: "intake@humantold.com", first_name: "Intake at Humantold" },
    };
    if (Boolean(process.env.REACT_APP_EMAIL_TEST)) {
        updated_outbound_email.subject = `[Intake system test] - ${updated_outbound_email.subject}`;
    }

    if (updated_outbound_email.cc && Array.isArray(updated_outbound_email.cc)) {
        updated_outbound_email.cc = updated_outbound_email.cc.filter((recipient) => {
            if (!isValidEmail(recipient.email)) {
                console.log({
                    msg: `Invalid cc email`,
                    invalid_email: `${recipient.email}`,
                });
                return false;
            }
            return true;
        });
    }

    return {
        id,
        email: sanitiseEmailRecipients(updated_outbound_email),
    };
}

export async function queueEmail(
    outbound_email: INewOutboundEmail,
    status: OutboundEmailStatus = OutboundEmailStatus.UNSCHEDULED
) {
    const queuedEmail = getQueuedEmail(outbound_email, status);
    await db
        .collection(Collection.OUTBOUND_EMAILS)
        .doc(queuedEmail.id)
        .set({ ...queuedEmail.email });
}

export async function queueConsentEmail(
    outboundEmail: INewOutboundEmail,
    status: OutboundEmailStatus,
    clientId: string,
    appointmentId: string,
    consentForm: ConsentForm,
    consentFormType: PortalSupportedFormType | undefined,
    lastUpdatedBy: string,
    options: {
        isReminder: boolean;
    } = { isReminder: false }
) {
    const queuedEmail = getQueuedEmail(outboundEmail, status);
    const batch = writeBatch(dbv9);
    const updatePayload: UpdateData<DocumentData> = {
        [`appointment_checks.${appointmentId}.consent_form`]: consentForm,
        date_updated: serverTimestamp(),
        last_updated_by: lastUpdatedBy,
        updated_from: UpdatedFrom.INTAKE,
    };

    if (consentFormType) {
        updatePayload[`appointment_checks.${appointmentId}.consent_form_type`] =
            consentFormType;
    }

    if (options.isReminder) {
        updatePayload[
            `appointment_checks.${appointmentId}.consent_form_reminder_sent`
        ] = serverTimestamp();
        updatePayload[
            `appointment_checks.${appointmentId}.consent_form_reminder_count`
        ] = increment(1);
    } else {
        updatePayload[`appointment_checks.${appointmentId}.consent_form_sent`] =
            serverTimestamp();
    }

    batch.set(
        doc(dbv9, Collection.OUTBOUND_EMAILS, queuedEmail.id),
        queuedEmail.email
    );
    batch.update(doc(dbv9, Collection.CLIENTS, clientId), updatePayload);

    await batch.commit();
}

export async function queueConsentReminderEmail(
    outboundEmail: INewOutboundEmail,
    status: OutboundEmailStatus,
    clientId: string,
    appointmentId: string,
    consentForm: ConsentForm,
    consentFormType: PortalSupportedFormType | undefined,
    lastUpdatedBy: string
) {
    await queueConsentEmail(
        outboundEmail,
        status,
        clientId,
        appointmentId,
        consentForm,
        consentFormType,
        lastUpdatedBy,
        { isReminder: true }
    );
}

export async function fetchOutboundEmails(
    client_id = "",
    statuses: OutboundEmailStatus[] = [
        OutboundEmailStatus.UNSCHEDULED,
        OutboundEmailStatus.PENDING,
    ],
    types: OutboundEmailType[] = [],
    pageSize: number = 50,
    pageCursor?: firebase.firestore.QueryDocumentSnapshot,
    recipientType?: EmailRecipientType
): Promise<{
    records: INewOutboundEmail[];
    nextPageCursor?: firebase.firestore.QueryDocumentSnapshot;
}> {
    let query = db
        .collection(Collection.OUTBOUND_EMAILS)
        .where("status", "in", statuses);

    if (types.length > 0) {
        query = query.where("type", "in", types);
    }

    if (client_id) {
        query = query.where("client_id", "==", client_id);
    }

    if (recipientType) {
        query = query.where("recipient_type", "==", recipientType);
    }

    query = query.orderBy("created_at", "desc");

    if (pageCursor) {
        query = query.startAfter(pageCursor);
    }

    const querySnapshot = await query.limit(pageSize + 1).get();
    const pageDocuments = querySnapshot.docs.slice(0, pageSize);

    return {
        records: pageDocuments.map((document) => {
            return document.data() as INewOutboundEmail;
        }),
        nextPageCursor:
            querySnapshot.docs.length > pageSize
                ? pageDocuments[pageDocuments.length - 1]
                : undefined,
    };
}

export async function updateOutboundEmailStatus(
    outbound_email: IOutboundEmail,
    status: OutboundEmailStatus = OutboundEmailStatus.PENDING
) {
    const outbound_email_doc_ref = db
        .collection(Collection.OUTBOUND_EMAILS)
        .doc(outbound_email.id);
    await outbound_email_doc_ref.update({
        ...sanitiseEmailRecipients(outbound_email),
        status,
        updated_at: new Date(),
    });
}

export async function deleteOutboundEmail(outbound_email_id: string) {
    await db.collection(Collection.OUTBOUND_EMAILS).doc(outbound_email_id).delete();
}

export async function addEmailReminder(email_reminder: IEmailReminder) {
    await db
        .collection(Collection.OUTBOUND_REMINDER_EMAILS)
        .doc(email_reminder.id)
        .set({ ...email_reminder });
}

// Reset a retryable outbound email by recreating it so the onCreate
// Cloud Function fires. Delete the original in the same transaction so stale
// rows cannot be retried repeatedly.
export async function retryOutboundEmail(outbound_email_id: string) {
    const ref = db.collection(Collection.OUTBOUND_EMAILS).doc(outbound_email_id);
    const newId = uuidv4();

    return db.runTransaction(async (transaction) => {
        const snap = await transaction.get(ref);
        if (!snap.exists) return;

        const existing = snap.data() as IOutboundEmail;
        if (
            existing.status !== OutboundEmailStatus.FAILED &&
            existing.status !== OutboundEmailStatus.UNSCHEDULED
        ) {
            return;
        }

        transaction.set(db.collection(Collection.OUTBOUND_EMAILS).doc(newId), {
            ...sanitiseEmailRecipients(existing),
            id: newId,
            status: OutboundEmailStatus.PENDING,
            retries: 0,
            last_error: {},
            created_at: new Date(),
            updated_at: new Date(),
        });
        transaction.delete(ref);

        return newId;
    });
}
